import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { DEFAULT_ROWS_PER_PAGE } from "constants/paginationConstants";
import { useRTL } from "hooks/isRTL";
import DeliveryPricingForm from "Components/DeliveryPricing/DeliveryPricingForm";
import { useParams } from "react-router-dom";
import { OrderTypeEnum, PricingOptionTypeEnum } from "enums/shared";
import {
  getAllDeliveryPricingOptions,
  activateDeliveryPricingOption,
  createDeliveryPricingOption,
  updateDeliveryPricingOption,
  // deleteDeliveryPricingOption,
} from "services/deliveryPricing";

const DeliveryPricingPage = () => {
  useRTL();
  const DEFAULT_PRICING_PAGE = 1;
  const { orderType: orderTypeParam } = useParams();
  const selectedOrderType = useMemo(() => {
    const normalized = (orderTypeParam || "").toLowerCase();
    return normalized === "regular" ? OrderTypeEnum.regular : OrderTypeEnum.expedited;
  }, [orderTypeParam]);
  const [activeTab, setActiveTab] = useState(0);
  const [options, setOptions] = useState([]);
  const [senderOptions, setSenderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedOption, setSelectedOption] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(DEFAULT_PRICING_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [senderPage, setSenderPage] = useState(DEFAULT_PRICING_PAGE);
  const [senderRowsPerPage, setSenderRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [senderTotalCount, setSenderTotalCount] = useState(0);

  const fetch = useCallback(
    async (
      pageNum = DEFAULT_PRICING_PAGE,
      pageSize = DEFAULT_ROWS_PER_PAGE,
      orderType = selectedOrderType
    ) => {
      setLoading(true);
      try {
        const data = await getAllDeliveryPricingOptions(
          pageNum,
          pageSize,
          orderType,
          false
        );
        const allOptions = data.items || data.page || data || [];
        // Filter to exclude sender-related options
        const generalOptions = allOptions.filter(opt => !opt.isSenderOption);
        setOptions(generalOptions);
        setTotalCount(generalOptions.length);
        setError(null);
      } catch (err) {
        console.error("Failed to load delivery pricing options", err);
        setError("Failed to load options. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [selectedOrderType]
  );

  const fetchSenderOptions = useCallback(
    async (
      pageNum = DEFAULT_PRICING_PAGE,
      pageSize = DEFAULT_ROWS_PER_PAGE,
      orderType = selectedOrderType
    ) => {
      setLoading(true);
      try {
        // Filtering options that have isSenderOption = true
        const data = await getAllDeliveryPricingOptions(
          pageNum,
          pageSize,
          orderType,
          true
        );
        const filtered = (data.items || data.page || data || []).filter(opt => opt.isSenderOption);
        setSenderOptions(filtered);
        setSenderTotalCount(filtered.length);
        setError(null);
      } catch (err) {
        console.error("Failed to load sender pricing options", err);
        setError("Failed to load sender options. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [selectedOrderType]
  );

  useEffect(() => {
    setPage(DEFAULT_PRICING_PAGE);
    setSenderPage(DEFAULT_PRICING_PAGE);
  }, [selectedOrderType]);

  useEffect(() => {
    if (activeTab === 0) {
      fetch(page, rowsPerPage, selectedOrderType);
    } else {
      fetchSenderOptions(senderPage, senderRowsPerPage, selectedOrderType);
    }
  }, [activeTab, page, rowsPerPage, senderPage, senderRowsPerPage, selectedOrderType, fetch, fetchSenderOptions]);

  const handleOpenCreateDialog = () => {
    setDialogMode("create");
    setSelectedOption(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (option) => {
    setDialogMode("edit");
    setSelectedOption(option);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOption(null);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      const isSenderOption = activeTab === 1;

      if (dialogMode === "create") {
        await createDeliveryPricingOption(
          {
            ...formData,
            isSenderOption,
            IsSenderOption: isSenderOption,
          },
          selectedOrderType
        );
      } else {
        await updateDeliveryPricingOption(
          {
            id: selectedOption.id,
            ...formData,
            isSenderOption,
            IsSenderOption: isSenderOption,
          },
          selectedOrderType
        );
      }
      if (activeTab === 0) {
        setPage(DEFAULT_PRICING_PAGE);
        await fetch(DEFAULT_PRICING_PAGE, rowsPerPage, selectedOrderType);
      } else {
        setSenderPage(DEFAULT_PRICING_PAGE);
        await fetchSenderOptions(
          DEFAULT_PRICING_PAGE,
          senderRowsPerPage,
          selectedOrderType
        );
      }
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save option", err);
      setError("Failed to save. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateDeliveryPricingOption(id, selectedOrderType);
      setSuccess("Activated successfully");
      setError(null);
      if (activeTab === 0) {
        fetch(page, rowsPerPage, selectedOrderType);
      } else {
        fetchSenderOptions(senderPage, senderRowsPerPage, selectedOrderType);
      }
    } catch (err) {
      console.error(err);
      setSuccess(null);
      setError("Failed to activate. Please try again.");
    }
  };

  // Delete option removed

  const handleChangePage = (event, newPage) => {
    if (activeTab === 0) {
      setPage(newPage);
    } else {
      setSenderPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    if (activeTab === 0) {
      setRowsPerPage(value);
      setPage(DEFAULT_PRICING_PAGE);
    } else {
      setSenderRowsPerPage(value);
      setSenderPage(DEFAULT_PRICING_PAGE);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPricingOptionTypeLabel = (value) => {
    const normalized = Number(value);
    if (normalized === PricingOptionTypeEnum.PerDelivery) return "Per Delivery";
    if (normalized === PricingOptionTypeEnum.PerDistance) return "Per Distance";
    return "";
  };

  const formatSenderNames = (opt) => {
    const raw =
      opt?.senderNames ??
      opt?.SenderNames ??
      opt?.senderNameList ??
      opt?.SenderNameList ??
      opt?.senderNamesList ??
      opt?.SenderNamesList ??
      opt?.senders ??
      opt?.Senders ??
      opt?.senderList ??
      opt?.SenderList;

    if (Array.isArray(raw)) {
      const names = raw
        .map((s) =>
          s?.fullName ||
          s?.name ||
          s?.fullNameAr ||
          s?.fullNameEn ||
          s?.FullName ||
          s?.Name ||
          s
        )
        .filter(Boolean)
        .map(String);
      return names.length ? names.join(", ") : "—";
    }

    if (typeof raw === "string") return raw || "—";
    if (raw && typeof raw === "object") {
      const val = raw.fullName || raw.name || raw.FullName || raw.Name;
      return val ? String(val) : "—";
    }

    return "—";
  };

  return (
    <Box sx={{ padding: "2rem", position: "relative" }}>
      <Typography variant="h1" sx={{ marginBottom: "1rem" }}>
        {selectedOrderType === OrderTypeEnum.expedited
          ? "Expedited Delivery Pricing Options"
          : "Regular Delivery Pricing Options"}
      </Typography>

      {error && <Alert severity="error" sx={{ marginBottom: "1rem" }}>{error}</Alert>}
      <Snackbar open={Boolean(success)} autoHideDuration={3000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Tabs with Create Button */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: 1, borderColor: "divider", marginBottom: "1.5rem" }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="General Options" />
          <Tab label="Business Options" />
        </Tabs>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ backgroundColor: "#2A3467", marginRight: "1rem" }}
        >
          Create
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ marginTop: "1rem", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Option #</TableCell>
                {activeTab === 1 && <TableCell>Sender Names</TableCell>}
                <TableCell>Pricing Option Type</TableCell>
                <TableCell>Base Km</TableCell>
                <TableCell>Base Price</TableCell>
                <TableCell>Increment Start After Km</TableCell>
                <TableCell>Increment Step Km</TableCell>
                <TableCell>Increment Step Price</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                    {(activeTab === 0 ? options : senderOptions).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 1 ? 10 : 9} sx={{ textAlign: "center", padding: "2rem" }}>
                    {activeTab === 0 ? "No general pricing options available" : "No business pricing options available"}
                  </TableCell>
                </TableRow>
              ) : (
                (activeTab === 0 ? options : senderOptions).map((opt) => (
                  <TableRow key={opt.id}>
                    <TableCell>{opt.optionNumber}</TableCell>
                    {activeTab === 1 && <TableCell>{formatSenderNames(opt)}</TableCell>}
                    <TableCell>{getPricingOptionTypeLabel(opt.pricingOptionType ?? opt.PricingOptionType)}</TableCell>
                    <TableCell>{opt.baseKm}</TableCell>
                    <TableCell>{opt.basePrice}</TableCell>
                    <TableCell>{opt.incrementStartAfterKm ?? opt.incrementStartKm ?? opt.IncrementStartKm ?? ""}</TableCell>
                    <TableCell>{opt.incrementStepKm ?? opt.stepKm ?? opt.StepKm ?? ""}</TableCell>
                    <TableCell>{opt.incrementStepPrice ?? opt.incrementAmount ?? opt.IncrementAmount ?? opt.firstIncrementAmount ?? opt.FirstIncrementAmount ?? ""}</TableCell>
                    <TableCell>{opt.isActive ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEditDialog(opt)}
                        >
                          Edit
                        </Button>
                        {!opt.isActive && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleActivate(opt.id)}
                          >
                            Activate
                          </Button>
                        )}
                        {/* Delete option removed */}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={activeTab === 0 ? totalCount : senderTotalCount}
            rowsPerPage={activeTab === 0 ? rowsPerPage : senderRowsPerPage}
            page={activeTab === 0 ? page : senderPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === "create" ? "Create New Pricing Option" : "Edit Pricing Option"}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "2rem" }}>
          <DeliveryPricingForm
            initialData={selectedOption}
            onSubmit={handleFormSubmit}
            isLoading={formLoading}
            showSenderField={activeTab === 1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryPricingPage;
